import type { CSSProperties, MouseEvent } from 'react';
import type {
  LinkProps as ReactRouterLinkProps,
  NavLinkProps as ReactRouterNavLinkProps,
} from 'react-router-dom';
import { NavLink, Link as RouterLink } from 'react-router-dom';

import classNames from 'classnames';

import styles from './Link.module.css';

export type LinkBaseColor = 'carrot' | 'kale' | 'blueberry' | 'white';

export type LinkSharedProps = {
  className?: string;
  style?: CSSProperties;
  /**
   * The base color of the link (not hovered).
   *
   * @defaultValue carrot
   */
  baseColor?: LinkBaseColor;
};

export type AnchorProps = JSX.IntrinsicElements['a'] & LinkSharedProps;

export type ExternalLinkProps = Omit<AnchorProps, 'href'> &
  LinkSharedProps & {
    to: string;
  };

// Remove the function type from className react router props
export type RouterLinkProps = ReactRouterLinkProps & LinkSharedProps;
export type NavLinkProps = ReactRouterNavLinkProps & LinkSharedProps;

function isAnchorLink(props: LinkProps): props is AnchorProps {
  return 'href' in props;
}

function isExternalLink(props: LinkProps): props is ExternalLinkProps {
  if (isAnchorLink(props)) {
    return false;
  }

  const schemes = ['http:', 'https:', 'mailto:', 'tel:', 'sms:'];

  return schemes.some(scheme => String(props.to).startsWith(scheme));
}

function isRouterNavLink(props: LinkProps): props is NavLinkProps {
  return 'activeClassName' in props || 'activeStyle' in props;
}

function isRouterLink(props: LinkProps): props is RouterLinkProps {
  return !isRouterNavLink(props) && 'to' in props;
}

export type LinkProps =
  | AnchorProps
  | ExternalLinkProps
  | RouterLinkProps
  | NavLinkProps;

/**
 * Create an anchor link, a react-router `NavLink` or a react-router `Link`
 * according to the props that are passed in.
 *
 * When `aria-disabled` is passed in, the `onClick` event is disabled.
 *
 * @remarks
 * Please note this component has different props from the
 * {@link https://corgi.tfd.engineering/?path=/story/components-link--example | legacy
 * version}.
 * @cssVariable --tfd-link-color The default color of the link
 * @cssVariable --tfd-link-active-color The color of the link when hovered, actived, or focused
 * @cssVariable --tfd-link-font The default font family, size, weight, and line height of the link
 * @cssVariable --tfd-link-letter-spacing The letter spacing of the link
 * @cssVariable --tfd-link-text-decoration The default text decoration of the link, e.g. underlined
 * @see https://corgi-x.tfd.engineering/components/link
 */
export function Link(props: LinkProps) {
  let handleClick = props.onClick;

  const { baseColor, ...restProps } = props;

  const className = classNames(
    styles.root,
    baseColor && styles[baseColor],
    props.className
  );

  if (props['aria-disabled']) {
    handleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
  }

  if (isAnchorLink(restProps)) {
    return <a {...restProps} className={className} onClick={handleClick} />;
  }

  if (isExternalLink(restProps)) {
    const { to, ...externalLinkProps } = restProps;
    return (
      <a
        {...externalLinkProps}
        className={className}
        href={to}
        onClick={handleClick}
      />
    );
  }

  if (isRouterNavLink(restProps)) {
    return (
      <NavLink {...restProps} className={className} onClick={handleClick} />
    );
  }

  if (isRouterLink(restProps)) {
    return (
      <RouterLink {...restProps} className={className} onClick={handleClick} />
    );
  }

  return <></>;
}
